import { useEffect, useMemo } from 'react';
import { ConfigProvider, App as AntdApp } from 'antd';
import enUS from 'antd/locale/en_US';
import arEG from 'antd/locale/ar_EG';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import 'dayjs/locale/en';
import { RoleProvider } from '@/auth/roleContext';
import { AppRoutes } from '@/routes';
import { buildAntdTheme } from '@/theme/antdConfig';

/**
 * Root component. Wires ConfigProvider (direction + locale) to i18next.
 * Updates <html dir> and <html lang> on every locale change (NFR-003).
 *
 * Theme tokens sourced from src/theme/tokens.ts via buildAntdTheme —
 * keep this file free of inline theme literals. (Design-system invariant.)
 */
export default function App() {
  const { i18n } = useTranslation();
  const isArabic = Boolean(i18n.language?.startsWith('ar'));
  const direction: 'rtl' | 'ltr' = isArabic ? 'rtl' : 'ltr';
  const antdLocale = isArabic ? arEG : enUS;

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('dir', direction);
    root.setAttribute('lang', isArabic ? 'ar' : 'en');
    dayjs.locale(isArabic ? 'ar' : 'en');
  }, [direction, isArabic]);

  const themeConfig = useMemo(() => buildAntdTheme({ isArabic }), [isArabic]);

  return (
    <ConfigProvider direction={direction} locale={antdLocale} theme={themeConfig}>
      <AntdApp>
        <RoleProvider>
          <AppRoutes />
        </RoleProvider>
      </AntdApp>
    </ConfigProvider>
  );
}
